import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdSend, MdAttachFile, MdEmojiEmotions } from 'react-icons/md';

const MAX_LINES = 6;
const LINE_HEIGHT = 24;

const COMMON_EMOJIS = ['😀', '😊', '😂', '❤️', '👍', '👋', '😍', '🙏', '😅', '😢', '😎', '🔥', '✨', '💪', '🎉', '✅', '❌', '💯', '😘', '🥰'];

export default function MessageComposer({
  onSubmit,
  replyPreview,
  onCancelReply,
  disabled,
  placeholder = 'Unesite poruku...',
  onAttachmentClick,
  t = (s) => s,
}) {
  const [value, setValue] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiPickerRect, setEmojiPickerRect] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const emojiPickerRef = useRef(null);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const lines = Math.min(MAX_LINES, (ta.scrollHeight / LINE_HEIGHT) | 0);
    ta.style.height = `${Math.max(44, lines * LINE_HEIGHT)}px`;
  }, [value]);

  useEffect(() => {
    if (!emojiOpen) {
      setEmojiPickerRect(null);
      return;
    }
    if (emojiButtonRef.current) {
      const r = emojiButtonRef.current.getBoundingClientRect();
      setEmojiPickerRect({
        bottom: window.innerHeight - r.top + 10,
        left: Math.min(r.left, window.innerWidth - 250),
        width: 220,
      });
    }
    const handleClickOutside = (e) => {
      const btn = emojiButtonRef.current;
      const picker = emojiPickerRef.current;
      if (btn?.contains(e.target) || picker?.contains(e.target)) return;
      setEmojiOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [emojiOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = value.trim();
    if (!text || disabled) return;
    onSubmit(text);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleAttachmentClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && onAttachmentClick) onAttachmentClick(file);
  };

  const insertEmoji = (emoji) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const after = value.slice(end);
    setValue(before + emoji + after);
    setEmojiOpen(false);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      {replyPreview && (
        <div className="flex items-start justify-between gap-2 mb-2 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700/50">
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1">
            Odgovor: {replyPreview.content}
          </p>
          <button
            type="button"
            onClick={onCancelReply}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-0.5"
            aria-label="Otkaži odgovor"
          >
            ×
          </button>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        aria-hidden
      />
      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={handleAttachmentClick}
          className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          aria-label="Priloži sliku"
          disabled={disabled}
        >
          <MdAttachFile className="text-xl" />
        </button>
        <div className="relative">
          <button
            ref={emojiButtonRef}
            type="button"
            onClick={() => setEmojiOpen((v) => !v)}
            className={`p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${emojiOpen ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
            aria-label="Emoji"
            aria-expanded={emojiOpen}
            disabled={disabled}
          >
            <MdEmojiEmotions className="text-xl" />
          </button>
          {emojiOpen && emojiPickerRect && createPortal(
            <div
              ref={emojiPickerRef}
              className="fixed p-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl z-[99999] grid grid-cols-5 gap-1.5 max-h-48 overflow-y-auto"
              style={{
                bottom: emojiPickerRect.bottom,
                left: emojiPickerRect.left,
                width: emojiPickerRect.width,
                fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "EmojiSymbols", sans-serif',
              }}
              role="listbox"
            >
              {COMMON_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => insertEmoji(emoji)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-2xl leading-none"
                  role="option"
                >
                  {emoji}
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          className="flex-1 min-h-[44px] max-h-[144px] resize-none rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          aria-label="Poruka"
        />
        <button
          type="submit"
          disabled={!value.trim() || disabled}
          className="p-2.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 flex-shrink-0"
          aria-label="Pošalji"
        >
          <MdSend className="text-xl" />
        </button>
      </div>
    </form>
  );
}
